import { useCallback, useMemo, useRef } from 'react'

import dayjs from 'dayjs'
import { useLocalStorage } from 'usehooks-ts'

import { getMainPerformer } from '@/utils/production'

import { MAX_RECENTLY_VIEWED, RECENTLY_VIEWED_PERFORMERS_KEY, RECENTLY_VIEWED_PRODUCTIONS_KEY } from './constants'
import {
    RecentlyViewedData,
    RecentlyViewedEntry,
    RecentlyViewedInput,
    RecentlyViewedPerformer,
    RecentlyViewedProduction,
    RecentPerformerInput,
    RecentProductionInput,
} from './types'

const buildProduction = (entry: RecentProductionInput): RecentlyViewedProduction => {
    const { id, name, webPath, venue, formattedDate, utcDate } = entry
    const { id: performerId, name: performerName } = getMainPerformer(entry)
    return {
        id,
        name,
        webPath,
        venue,
        formattedDate,
        utcDate,
        performer: { id: performerId, name: performerName },
    }
}

const buildPerformer = (entry: RecentPerformerInput): RecentlyViewedPerformer => {
    const { id, name, webPath, category } = entry
    return {
        id,
        name,
        webPath,
        category,
    }
}

const isProduction = (entry: RecentlyViewedEntry | RecentlyViewedInput) => {
    return 'venue' in entry
}

const isPerformer = (entry: RecentlyViewedEntry | RecentlyViewedInput) => {
    return 'category' in entry
}

const isProductionNew = (entry: RecentlyViewedProduction) => {
    const productionDate = dayjs(entry.utcDate)
    const date = dayjs()
    return productionDate.isAfter(date)
}

export const useRecentlyViewed = (): RecentlyViewedData => {
    const [productions, setProductions] = useLocalStorage<RecentlyViewedProduction[]>(
        RECENTLY_VIEWED_PRODUCTIONS_KEY,
        [],
    )
    const [performers, setPerformers] = useLocalStorage<RecentlyViewedPerformer[]>(RECENTLY_VIEWED_PERFORMERS_KEY, [])
    const idRef = useRef(
        JSON.stringify({
            performers: performers.map((p) => p.id),
            productions: productions.map((p) => p.id),
        }),
    )

    // trim old productions
    const newProductions = useMemo(() => productions.filter(isProductionNew), [productions])
    if (newProductions.length != productions.length) setProductions(newProductions)

    const addRecentlyViewed = useCallback(
        (entry: RecentlyViewedInput) => {
            const { id } = entry
            const idMatcher = (entry: { id: number }) => entry.id == id
            if (isProduction(entry)) {
                if (!productions.find(idMatcher)) {
                    if (productions.length >= MAX_RECENTLY_VIEWED) {
                        productions.pop()
                    }
                    setProductions([buildProduction(entry as RecentProductionInput), ...productions])
                }
            } else if (isPerformer(entry)) {
                if (!performers.find(idMatcher)) {
                    if (performers.length >= MAX_RECENTLY_VIEWED) {
                        performers.pop()
                    }
                    setPerformers([buildPerformer(entry as RecentPerformerInput), ...performers])
                }
            }
        },
        // Use immutable primitive value as dependency to prevent excessive re-renders
        // with multiple tabs updating same local storage item
        [idRef.current],
    )

    const removeRecentlyViewed = (entry: RecentlyViewedEntry) => {
        const { id } = entry
        const idMatcher = (entry: { id: number }) => entry.id == id
        if (isProduction(entry)) {
            if (productions.find(idMatcher)) {
                setProductions(productions.filter((p) => !idMatcher(p)))
            }
        } else if (isPerformer(entry)) {
            if (performers.find(idMatcher)) {
                setPerformers(performers.filter((p) => !idMatcher(p)))
            }
        }
    }

    return {
        performers,
        productions,
        addRecentlyViewed,
        removeRecentlyViewed,
    }
}
